import { useRef, useState } from 'react'
import { Maximize2, X } from 'lucide-react'
import { Modal, ModalContent } from '@heroui/react'

/**
 * A competition poster, always shown whole.
 *
 * Posters are designed artwork: the name sits at the top, the date at the
 * bottom, and cropping to fill a card cut both off. So the poster is fitted
 * inside its frame, never cropped, and whatever the frame has left over is
 * filled with a blurred, enlarged copy of the same poster. The frame keeps its
 * shape in every list, and there is no letterbox bar in sight.
 *
 * `fit="frame"` fills the box the caller sized (a card, a thumbnail).
 * `fit="natural"` lets the poster set its own height, up to `maxHeight`, for a
 * page header where the whole thing should simply be there.
 *
 * With `zoomable`, a tap opens the full-screen viewer. Inside a link (a card),
 * pass `zoomable="button"`: a small corner button opens it and the rest of the
 * card still navigates.
 */

interface Labels {
  /** Accessible name of the control that opens the viewer. */
  open: string
  close: string
  /** The hint pill in the viewer, e.g. "Tap to zoom". */
  zoomHint: string
}

interface Props {
  src: string
  alt?: string
  fit?: 'frame' | 'natural'
  /** Only for `fit="natural"`. Any CSS length. */
  maxHeight?: string
  zoomable?: false | 'image' | 'button'
  labels?: Labels
  className?: string
}

const DEFAULT_LABELS: Labels = { open: 'View poster', close: 'Close', zoomHint: 'Tap to zoom' }

export function PosterImage({
  src,
  alt = '',
  fit = 'frame',
  maxHeight = '70vh',
  zoomable = false,
  labels = DEFAULT_LABELS,
  className = '',
}: Props) {
  const [open, setOpen] = useState(false)

  const openViewer = (e: React.MouseEvent) => {
    // Inside a card this sits in a <Link>: open the viewer, do not navigate.
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
  }

  const picture = (
    <>
      {/* The ambient fill: the poster itself, blown up and blurred. */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover opacity-70 blur-2xl"
      />
      <img
        src={src}
        alt={alt}
        className={
          fit === 'frame'
            ? 'relative h-full w-full object-contain drop-shadow-xl'
            : 'relative mx-auto block h-auto w-auto max-w-full object-contain drop-shadow-xl'
        }
        style={fit === 'natural' ? { maxHeight } : undefined}
      />
    </>
  )

  return (
    <div
      className={`relative overflow-hidden bg-neutral-900 ${fit === 'frame' ? 'h-full w-full' : 'w-full'} ${className}`}
    >
      {zoomable === 'image' ? (
        <button
          type="button"
          onClick={openViewer}
          aria-label={labels.open}
          className="group block h-full w-full cursor-zoom-in"
        >
          {picture}
          <ExpandBadge className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
        </button>
      ) : (
        picture
      )}

      {zoomable === 'button' && (
        <button
          type="button"
          onClick={openViewer}
          aria-label={labels.open}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white shadow-lg ring-1 ring-white/20 backdrop-blur-md transition-transform hover:scale-110 active:scale-95"
        >
          <ExpandIcon />
        </button>
      )}

      <PosterViewer
        isOpen={open}
        src={src}
        alt={alt}
        labels={labels}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}

function ExpandBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`pointer-events-none absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white shadow-lg ring-1 ring-white/20 backdrop-blur-md ${className}`}
    >
      <ExpandIcon />
    </span>
  )
}

function ExpandIcon() {
  return <Maximize2 className="h-4 w-4" aria-hidden="true" />
}

/** How far a tap zooms in. Enough to read the small print on a poster. */
const ZOOM = 2.5

/**
 * The full-screen viewer. The poster rises out of its own blurred glow; a
 * click or tap zooms in on the spot you touched, and from there the poster
 * follows the pointer (or your finger) so you can read it corner to corner.
 * Escape, the close button or a tap outside the poster closes it.
 *
 * Built on HeroUI's Modal, which already owns the portal, focus, Escape and
 * scroll locking; this only supplies what goes inside it.
 */
function PosterViewer({
  isOpen,
  src,
  alt,
  labels,
  onClose,
}: {
  isOpen: boolean
  src: string
  alt: string
  labels: Labels
  onClose: () => void
}) {
  const [zoomed, setZoomed] = useState(false)
  // Where the zoom is anchored, as a percentage of the poster's box.
  const [origin, setOrigin] = useState({ x: 50, y: 50 })
  const imgRef = useRef<HTMLImageElement>(null)

  function close() {
    setZoomed(false)
    onClose()
  }

  function originFrom(clientX: number, clientY: number) {
    const box = imgRef.current?.getBoundingClientRect()
    if (!box || !box.width || !box.height) return { x: 50, y: 50 }
    const clamp = (n: number) => Math.min(100, Math.max(0, n))
    return {
      x: clamp(((clientX - box.left) / box.width) * 100),
      y: clamp(((clientY - box.top) / box.height) * 100),
    }
  }

  function toggleZoom(e: React.MouseEvent) {
    e.stopPropagation()
    setOrigin(originFrom(e.clientX, e.clientY))
    setZoomed((z) => !z)
  }

  function pan(e: React.PointerEvent) {
    // Mouse: the poster follows the cursor. Touch: it follows a drag.
    if (!zoomed || (e.pointerType !== 'mouse' && e.buttons === 0)) return
    setOrigin(originFrom(e.clientX, e.clientY))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="full"
      hideCloseButton
      classNames={{
        base: 'bg-transparent shadow-none rounded-none m-0',
        wrapper: 'z-[1000]',
        backdrop: 'z-[1000] bg-black/80 backdrop-blur-sm',
      }}
    >
      <ModalContent aria-label={alt || labels.open}>
        {/* The Modal portals out, but React still bubbles its clicks up to
            whatever rendered it, which on a card is a <Link>. Stop them here
            so the viewer never navigates. A click off the poster closes. */}
        <div
          className="relative flex h-full w-full items-center justify-center overflow-hidden"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            close()
          }}
        >
          {/* Ambient glow: the poster, huge and blurred. */}
          <img
            src={src}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full scale-150 object-cover opacity-50 blur-3xl"
          />

          <img
            ref={imgRef}
            src={src}
            alt={alt}
            draggable={false}
            onClick={toggleZoom}
            onPointerMove={pan}
            className={`relative max-h-[88vh] max-w-[92vw] select-none rounded-xl object-contain shadow-2xl ring-1 ring-white/10 transition-transform duration-300 ease-out ${
              zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            style={{
              transform: `scale(${zoomed ? ZOOM : 1})`,
              transformOrigin: `${origin.x}% ${origin.y}%`,
              // While zoomed a drag pans the poster instead of scrolling.
              touchAction: zoomed ? 'none' : 'auto',
            }}
          />

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              close()
            }}
            aria-label={labels.close}
            className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 backdrop-blur-md transition-colors hover:bg-white/20"
            style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>

          <span
            className={`pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-white/80 ring-1 ring-white/15 backdrop-blur-md transition-opacity duration-300 ${
              zoomed ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
          >
            {labels.zoomHint}
          </span>
        </div>
      </ModalContent>
    </Modal>
  )
}
